import { ContentState, convertToRaw, EditorState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import React, { useEffect } from "react";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { useAppContext } from "./../../context/AppCtxProvider";
import { makeThemedStyles } from "./../../viewcomponents/utils";
const useStyles = ({
  disableBorders,
  rtl,
}: {
  disableBorders: boolean;
  rtl: boolean;
}) =>
  makeThemedStyles("MilestoneInput.RichTextInput", (theme) => ({
    toolbar: {
      marginBottom: "0",
      borderTopLeftRadius: "1.22em",
      borderTopRightRadius: "1.22em",
      borderColor: "rgb(126, 135, 145, 0.27)",
      "& .rdw-dropdown-caretto": {
        "&open, &close": {
          right: rtl ? "unset" : "10%",
          left: !rtl ? "unset" : "10%",
        },
      },
    },
    editorWrapper: {
      marginTop: "0.5em",
      minWidth: "510px",
      width: "100%",
    },
    editorContainer: {
      width: "100%",
      minHeight: "200px",
      color: "#888",
      fontSize: "16px",
      cursor: "text",
      padding: "0.5em",
      backgroundColor: "#ffffff",
      border: disableBorders ? "" : "1px solid rgb(126, 135, 145, 0.27)",
      borderTopWidth: "0",
      borderRadius: "1.22em",
      borderTopLeftRadius: "0",
      borderTopRightRadius: "0",
      "& *": {
        margin: 0,
      },
      overflow: "hidden",
    },
  }));
interface Props {
  value?: string;
  onChange: (value: string) => void;
  onRawChange?: (value: ReturnType<typeof convertToRaw>) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}
function RichTextInput(props: Props) {
  const { value, onChange, placeholder, className, disabled } = props;
  const { rtl } = useAppContext();
  const classes = useStyles({ disableBorders: false, rtl })();
  const [editorState, setEditorState] = React.useState<EditorState>(
    EditorState.createEmpty()
  );
  function getHtml(editorState: EditorState) {
    return draftToHtml(convertToRaw(editorState.getCurrentContent()));
  }
  function getEditorState(html: string) {
    const contentBlock = htmlToDraft(html);
    const contentState = ContentState.createFromBlockArray(
      contentBlock.contentBlocks
    );
    return EditorState.createWithContent(contentState);
  }
  const onEditorStateChange = (editorState: EditorState) => {
    if (props.onRawChange) {
      props.onRawChange(convertToRaw(editorState.getCurrentContent()));
    }
    onChange(getHtml(editorState));
    setEditorState(editorState);
  };
  useEffect(() => {
    setEditorState(getEditorState(value ?? ""));
  }, [value]);
  return (
    <Editor
      readOnly={disabled}
      toolbarHidden={false}
      toolbarClassName={classes.toolbar}
      toolbar={{
        options: ["inline", "fontSize", "colorPicker", "list", "textAlign"],
        inline: {
          options: ["bold", "italic", "underline"],
        },
        list: { options: ["ordered", "unordered"] },
      }}
      editorState={editorState}
      placeholder={placeholder}
      wrapperClassName={classes.editorWrapper}
      editorClassName={`${classes.editorContainer} ${className ?? ""}`}
      onEditorStateChange={onEditorStateChange}
    />
  );
}
export default RichTextInput;
